import { ContentStatus, SimulationCaseList } from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { Button } from "common/buttons";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { Modal } from "common/components/Modal";
import { NoDataError } from "common/components/NoDataError";
import { Paragraph } from "common/components/Typography";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { SIMULATION_CASE_LIST_QUERY as QUERY } from "domain/common/simulation-mutations";
import { simulationBreadcrumb } from "domain/pages/breadcrumbs";
import { Schedule } from "domain/simulation/Schedule";
import {
  clinicalCategories,
  difficulties,
  modules,
} from "domain/simulation/utils";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import ReactSelect, { MultiValue } from "react-select";
import styles from "./SimulationPage.module.css";

interface FilterOption {
  value: string;
  label: string;
}

export const SimulationPage: React.FC = () => {
  const me = useAuth();
  const { data, loadingOrError } = useSimpleQuery<SimulationCaseList>(QUERY);
  const isAdmin = me.me?.menu.admin;
  const [moduleFilter, setModuleFilter] = useState<string[]>([]);
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [showCases, setShowCases] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const handleFilterChange = (
    choice: MultiValue<FilterOption>,
    setFilter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const newFilterArray = choice.map((filterSelect) => filterSelect.value);
    setFilter(newFilterArray);
  };

  const getCases = () => {
    let cases =
      data.simulationCaseList?.filter(
        (caseItem) => caseItem.status === ContentStatus.PUBLISHED,
      ) ?? [];

    if (moduleFilter.length > 0) {
      cases = cases.filter(
        (caseItem) => caseItem.module && moduleFilter.includes(caseItem.module),
      );
    }

    if (difficultyFilter.length > 0) {
      cases = cases.filter(
        (caseItem) =>
          caseItem.difficulty && difficultyFilter.includes(caseItem.difficulty),
      );
    }

    if (categoryFilter.length > 0) {
      cases = cases.filter(
        (caseItem) =>
          caseItem.clinicalCategory &&
          categoryFilter.includes(caseItem.clinicalCategory),
      );
    }

    return cases;
  };

  return (
    <NormalContentLayout
      title="Simuleringsbasert læring"
      breadcrumbItems={simulationBreadcrumb}
    >
      <Paragraph>
        Her kan du trene på å utføre kliniske konsultasjoner, og ta beslutninger
        tilknyttet diagnostisering og behandlingsvalg
      </Paragraph>

      {!showSchedule && (
        <>
          <ReactSelect
            options={modules}
            placeholder="Søker på alle moduler (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={(choice) => {
              handleFilterChange(choice, setModuleFilter);
            }}
          />

          <ReactSelect
            options={difficulties}
            placeholder="Søker på alle vanskelighetsgrader (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={(choice) => {
              handleFilterChange(choice, setDifficultyFilter);
            }}
          />

          <ReactSelect
            options={clinicalCategories}
            placeholder="Søker på alle kategorier (klikk her for å spesifisere)"
            isMulti={true}
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
            }}
            onChange={(choice) => {
              handleFilterChange(choice, setCategoryFilter);
            }}
          />
        </>
      )}

      <div className={styles.patientButtons}>
        <Button
          onClick={() => {
            setShowCases(!showCases);
            setShowSchedule(false);
          }}
        >
          Hent caser
        </Button>
        <p>eller</p>
        <Button
          onClick={() => {
            setShowSchedule(!showSchedule);
            setShowCases(false);
            setModuleFilter([]);
            setDifficultyFilter([]);
            setCategoryFilter([]);
          }}
        >
          Vis timebok
        </Button>
      </div>

      <div className={styles.caseButtons}>
        {showCases &&
          getCases().length > 0 &&
          getCases().map((caseItem) => (
            <Link key={caseItem.id} to={urls.simulationCase(caseItem.id)}>
              <Modal type="inline" kind="tall" title={caseItem.title}>
                {caseItem.patientInformation?.content ?? ""}
              </Modal>
            </Link>
          ))}
      </div>

      {showSchedule && <Schedule data={data.simulationCaseList} />}

      {isAdmin ? (
        <div>
          <Link to={urls.simulationAdminOverview}>Rediger caser</Link>
        </div>
      ) : null}
    </NormalContentLayout>
  );
};
